import {
  exifAnalysisSchema,
  jobSchema,
  originalsScanResultSchema,
  type ExifAnalysis,
  type ExifEraseRequest,
  type ExifPublishRequest,
  type Job,
  type OriginalsScanResult,
} from '@xphoto/shared';
import { ApiError } from '../gallery/api';
import { LONG_TIMEOUT_MS, apiFetch } from '../lib/http';

/**
 * Client typé de l'OUTIL EXIF & du tagging manuel (T4b, XPHOTO-24/-04/-05/-06).
 * Comme les autres clients, chaque réponse est RE-PARSÉE via son schéma partagé
 * `@xphoto/shared` (« never trust the client », y compris ses propres réponses) :
 * un backend divergent lève plutôt que de propager des données mal typées.
 */

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const body = (await res.json()) as { error?: { code?: string } };
      if (typeof body?.error?.code === 'string') code = body.error.code;
    } catch {
      // Corps non-JSON : on garde le code générique.
    }
    throw new ApiError(res.status, code);
  }
  return res.json();
}

/**
 * `GET /api/libraries/:id/exif/analysis` — présence par champ + divergence publication.
 * `folder` (optionnel) borne l'analyse au sous-arbre (même sémantique que la portée
 * d'effacement) → les compteurs ne reflètent que ce dossier.
 */
export async function fetchExifAnalysis(
  libraryId: string,
  folder?: string,
  signal?: AbortSignal,
): Promise<ExifAnalysis> {
  const qs = folder ? `?folder=${encodeURIComponent(folder)}` : '';
  const res = await apiFetch(`/api/libraries/${libraryId}/exif/analysis${qs}`, {
    signal: signal ?? null,
  });
  return exifAnalysisSchema.parse(await readJson(res));
}

/** `POST /api/libraries/:id/exif/publish` — enfile un job de publication IPTC/XMP (XPHOTO-06). */
export async function enqueueExifPublish(
  libraryId: string,
  body: ExifPublishRequest,
): Promise<Job> {
  const res = await apiFetch(`/api/libraries/${libraryId}/exif/publish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jobSchema.parse(await readJson(res));
}

/** `POST /api/libraries/:id/exif/erase` — enfile un job d'effacement sélectif (XPHOTO-04). */
export async function enqueueExifErase(libraryId: string, body: ExifEraseRequest): Promise<Job> {
  const res = await apiFetch(`/api/libraries/${libraryId}/exif/erase`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jobSchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/exif/originals/scan` — trouve/compte les sauvegardes
 * `_original` d'ExifTool (LECTURE ; source existante). `folder` = sous-arbre optionnel.
 */
export async function scanOriginals(
  libraryId: string,
  folder?: string,
): Promise<OriginalsScanResult> {
  const res = await apiFetch(`/api/libraries/${libraryId}/exif/originals/scan`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(folder ? { folder } : {}),
    // PARCOURS SYNCHRONE du système de fichiers : minutes possibles sur une grande
    // bibliothèque → plafond long, pas le défaut.
    timeoutMs: LONG_TIMEOUT_MS,
  });
  return originalsScanResultSchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/exif/originals/cleanup` — enfile un job de SUPPRESSION des
 * sauvegardes `_original` (ÉCRITURE ; 409 WRITE_DISABLED en lecture seule).
 */
export async function cleanupOriginals(
  libraryId: string,
  body: { folder?: string; dryRun?: boolean },
): Promise<Job> {
  const res = await apiFetch(`/api/libraries/${libraryId}/exif/originals/cleanup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jobSchema.parse(await readJson(res));
}
